/**
 * Pure step-manipulation helpers backing dnd-kit drag events in the protocol
 * builder. All functions are immutable — input arrays are never mutated.
 */
import { releaseStepTime, settle } from "@repo/domain";
import {
  type ActionFormValues,
  resolveBuilderStep,
  type StepFormValues,
} from "@/features/catalog/forms/use-plan-definition-form";
import type { EditableTimelineStep } from "@/shared/timeline";

let opCounter = 0;
function generateStepId(): string {
  opCounter++;
  return `step-${Date.now()}-${opCounter}-${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * AC-8: Reorder procedure cards within a single step.
 *
 * NOTE: This function must NEVER be wired to a drag handle. Procedures within a
 * step are concurrent — their displayed order is a display-only convenience.
 * There is no drag affordance for within-step ordering in the builder UI.
 */
export function moveProcedureWithinStep(
  steps: StepFormValues[],
  stepIdx: number,
  fromProcIdx: number,
  toProcIdx: number,
): StepFormValues[] {
  return steps.map((step, i) => {
    if (i !== stepIdx) return step;
    const actions = [...step.actions];
    const [moved] = actions.splice(fromProcIdx, 1);
    actions.splice(toProcIdx, 0, moved!);
    return { ...step, actions };
  });
}

/**
 * AC-9: Move a procedure from one step to another.
 * The procedure is inserted at `toInsertIdx` in the destination step.
 */
export function moveProcedureBetweenSteps(
  steps: StepFormValues[],
  fromStepIdx: number,
  fromProcIdx: number,
  toStepIdx: number,
  toInsertIdx: number,
): StepFormValues[] {
  const movedProc = steps[fromStepIdx]!.actions[fromProcIdx]!;
  return steps.map((step, i) => {
    if (i === fromStepIdx) {
      const actions = [...step.actions];
      actions.splice(fromProcIdx, 1);
      return { ...step, actions };
    }
    if (i === toStepIdx) {
      const actions = [...step.actions];
      actions.splice(toInsertIdx, 0, movedProc);
      return { ...step, actions };
    }
    return step;
  });
}

/**
 * AC-10: Extract a procedure into its own step inserted after the source step.
 * No-op when the step has only one procedure (nothing to split).
 */
export function splitProcedureFromStep(steps: StepFormValues[], stepIdx: number, procIdx: number): StepFormValues[] {
  const step = steps[stepIdx]!;
  if (step.actions.length <= 1) return [...steps];
  const extracted = step.actions[procIdx]!;
  const remaining = step.actions.filter((_, i) => i !== procIdx);
  const source = { ...step, actions: remaining };
  const newStep: StepFormValues = {
    id: generateStepId(),
    title: "",
    actions: [extracted],
    offsetMinutes: resolveBuilderStep(source).endMinutes,
  };
  const candidates = [...steps];
  candidates.splice(stepIdx, 1, source, newStep);
  // Empty draft cards have no timed extent to displace.
  const placed = candidates.flatMap((candidate) => {
    return candidate.actions.length ? [{ step: candidate, placement: resolveBuilderStep(candidate) }] : [];
  });
  const settled = settle(
    placed,
    placed.findIndex(({ step: current }) => current.id === newStep.id),
  );
  const byId = new Map(
    settled.map(({ step: current, placement }) => [current.id, { ...current, offsetMinutes: placement.offsetMinutes }]),
  );
  return candidates.map((current) => byId.get(current.id) ?? current);
}

/**
 * AC-11: Fold all procedures of `fromStep` into `intoStep` and remove `fromStep`.
 */
export function mergeSteps(steps: StepFormValues[], intoStepIdx: number, fromStepIdx: number): StepFormValues[] {
  const intoStep = steps[intoStepIdx]!;
  const fromStep = steps[fromStepIdx]!;
  const merged: StepFormValues = { ...intoStep, actions: [...intoStep.actions, ...fromStep.actions] };
  return steps.map((step, i) => (i === intoStepIdx ? merged : step)).filter((_, i) => i !== fromStepIdx);
}

/** Patch placements by identity; keep implicit durations and all card-owned data. */
export function applyTimelinePlacements(
  steps: StepFormValues[],
  before: readonly EditableTimelineStep[],
  after: readonly EditableTimelineStep[],
): StepFormValues[] {
  const previous = new Map(before.map((step) => [step.id, step.placement]));
  const updated = new Map(after.map((step) => [step.id, step.placement]));
  const originals = new Map(steps.map((step) => [step.id, step]));
  let visibleIndex = 0;
  return steps.map((original) => {
    if (!updated.has(original.id)) return original;
    const entry = after[visibleIndex++];
    const step = entry ? originals.get(entry.id) : undefined;
    if (!step) return original;
    const placement = updated.get(step.id);
    if (!placement) return step;
    return {
      ...step,
      offsetMinutes: placement.offsetMinutes,
      durationMinutes:
        placement.durationMinutes === previous.get(step.id)?.durationMinutes
          ? step.durationMinutes
          : placement.durationMinutes,
    };
  });
}

/**
 * Add a procedure while preserving the destination step duration.
 *
 * A null `stepIdx` means there is no step to join, so the procedure becomes a
 * step of its own at the end — the same reading `addOrder` gives an omitted
 * `concurrentWithStep`.
 */
export function addProcedureToStep(
  steps: StepFormValues[],
  stepIdx: number | null,
  action: ActionFormValues,
): StepFormValues[] {
  if (stepIdx === null) {
    const end = Math.max(
      0,
      ...steps.flatMap((step) => (step.actions.length ? [resolveBuilderStep(step).endMinutes] : [])),
    );
    return [...steps, { id: generateStepId(), title: "", actions: [action], offsetMinutes: end }];
  }
  const target = steps[stepIdx];
  if (!target) return [...steps];
  const added = { ...target, actions: [...target.actions, action] };
  return steps.map((step, i) => (i === stepIdx ? added : step));
}

/** Remove a timed step and return its allocation to the envelope. */
export function removeBuilderStep(steps: StepFormValues[], index: number, envelopeMinutes: number) {
  const timed = steps.flatMap((step) =>
    step.actions.length ? [{ ...step, placement: resolveBuilderStep(step), orders: [] }] : [],
  );
  const target = timed.findIndex((step) => step.id === steps[index]?.id);
  if (target < 0) return { steps: steps.filter((_, i) => i !== index), envelopeMinutes };
  const released = releaseStepTime(timed, new Set([target]), envelopeMinutes);
  const offsets = new Map(released.steps.map((step) => [step.id, step.placement.offsetMinutes]));
  return {
    steps: steps
      .filter((_, i) => i !== index)
      .map((step) => ({ ...step, offsetMinutes: offsets.get(step.id) ?? step.offsetMinutes })),
    envelopeMinutes: released.envelopeMinutes,
  };
}
